import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import MergeIcon from '@mui/icons-material/Merge'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import ReplayIcon from '@mui/icons-material/Replay'

import { theme } from '../../../themes/theme'
import { SecondaryButton } from '../../common/Button'
import { SEARCH_STATUS } from '../../../hooks/redaction/useRequestSearch'
import { categoryKey } from '../../../util/categoryConsts'
import {
  PANEL_BORDER,
  MUTED_TEXT_MID as MUTED,
  HIGHLIGHT_BG,
  SUBTLE_FILL,
  MUTED_ICON,
  HAIRLINE,
  SECONDARY_TEXT,
  SUCCESS,
  SUCCESS_LIGHT
} from '../../styles/colors'
import { ErrorMessage, OrangeErrorMessage, ProcessingMessage } from '../../common/UserMessage'

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  max-height: min(60vh, 620px);
  border: 1px solid ${PANEL_BORDER};
  border-radius: 10px;
  background: ${theme.colors.background.white};
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 10px;

  /* note: ::-webkit-scroll is non-standard */
  scrollbar-width: thin;
  scrollbar-color: rgba(58, 58, 58, 0.15) #fff;
  ::-webkit-scrollbar {
    width: 7px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(58, 58, 58, 0.15);
    border-radius: 6px;
  }
`

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  flex: 0 0 auto;
  background: ${theme.colors.background.white};
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px 12px 20px;
  border-bottom: 1px solid ${PANEL_BORDER};
`

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.body};
`

const CloseButton = styled.button`
  display: inline-flex;
  border: none;
  background: transparent;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  color: ${MUTED};

  :hover {
    background: ${SUBTLE_FILL};
    color: ${theme.colors.text.body};
  }

  .MuiSvgIcon-root {
    height: 18px;
    width: 18px;
  }
`

const CenteredState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 34px 20px;
  text-align: center;
  color: ${MUTED};
  font-size: 14px;

  .MuiSvgIcon-root {
    height: 36px;
    width: 36px;
    color: ${MUTED_ICON};
  }
`

const StateTitle = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${theme.colors.text.body};
`

const StateHint = styled.p`
  margin: 0;
  max-width: 520px;
  font-size: 13px;
`

const Spinner = styled(CircularProgress)`
  color: ${theme.colors.primary};
`

const SearchingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 26px 20px;
  color: ${MUTED};
  font-size: 14px;
`

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 14px 20px;
`

const FullWidthWrapper = styled.div`
  width: 100%;
`

const RetryButton = styled(SecondaryButton)`
  white-space: nowrap;
`

const GroupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 20px;
  background: ${theme.colors.background.light};
  border-top: 1px solid ${HAIRLINE};
  border-bottom: 1px solid ${HAIRLINE};
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.text.body};

  .MuiSvgIcon-root {
    height: 16px;
    width: 16px;
    color: ${MUTED};
  }
`

const GroupCount = styled.span`
  margin-left: auto;
  font-weight: 400;
  color: ${MUTED};
`

const ResultList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const ResultButton = styled.button`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  column-gap: 12px;
  row-gap: 4px;
  align-items: center;
  width: 100%;
  padding: 10px 14px 10px 20px;
  border: none;
  border-bottom: 1px solid ${HAIRLINE};
  background: ${theme.colors.background.white};
  text-align: left;
  font-family: inherit;
  color: ${theme.colors.text.body};
  cursor: pointer;

  :hover,
  :focus-visible {
    background: ${theme.colors.primaryExtraLight};
    outline: none;
  }

  li:last-of-type > & {
    border-bottom: none;
  }
`

const FileIcon = styled.span`
  display: inline-flex;
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: start;
  margin-top: 2px;
  color: ${MUTED};

  .MuiSvgIcon-root {
    height: 20px;
    width: 20px;
  }
`

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  grid-column: 2;
  grid-row: 1;
`

const Filename = styled.span`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  padding: 1px 9px 1px 7px;
  border-radius: 12px;
  border: 1px solid ${SUCCESS};
  background: ${SUCCESS_LIGHT};
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;

  .MuiSvgIcon-root {
    height: 13px;
    width: 13px;
    color: ${SUCCESS};
  }
`

const PageLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  grid-column: 3;
  grid-row: 1 / span 2;
  font-size: 13px;
  color: ${MUTED};
  white-space: nowrap;

  .MuiSvgIcon-root {
    height: 18px;
    width: 18px;
  }
`

const Snippet = styled.span`
  grid-column: 2;
  grid-row: 2;
  font-size: 13px;
  line-height: 18px;
  color: ${SECONDARY_TEXT};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FilenameMatch = styled(Snippet)`
  font-style: italic;
  color: ${MUTED};
`

const Highlight = styled.mark`
  background: ${HIGHLIGHT_BG};
  color: inherit;
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 500;
`

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  flex: 0 0 auto;
  background: ${theme.colors.background.white};
  border-radius: 0 0 10px 10px;
  padding: 9px 20px;
  border-top: 1px solid ${PANEL_BORDER};
  font-size: 12px;
  color: ${MUTED};
`

const MARKER_PATTERN = /\[\[(.+?)\]\]/g

// Convert server-side [[match]] markers to safe highlighted text.
export function renderHighlighted(snippet) {
  if (!snippet) return null
  const parts = []
  let lastIndex = 0
  let key = 0
  for (const match of snippet.matchAll(MARKER_PATTERN)) {
    if (match.index > lastIndex) parts.push(snippet.slice(lastIndex, match.index))
    parts.push(<Highlight key={key++}>{match[1]}</Highlight>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < snippet.length) parts.push(snippet.slice(lastIndex))
  return parts
}

// Preserve result ranking while grouping by category.
export function groupResultsByCategory(results) {
  const groups = new Map()
  for (const result of results) {
    if (!groups.has(result.categoryId)) {
      groups.set(result.categoryId, { categoryId: result.categoryId, categoryName: result.categoryName || 'Uncategorized', results: [] })
    }
    groups.get(result.categoryId).results.push(result)
  }
  return [...groups.values()]
}

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

function statusLabel(status) {
  return categoryKey[status]?.label ?? status
}

function ResultRow({ result, onSelect }) {
  const isCombined = result.status === 'COMBINED'
  const isFilenameMatch = result.matchType === 'filename'
  const pageText = result.page ? `p. ${result.page}` : isFilenameMatch ? 'p. 1' : ''
  const ariaLabel = `Open ${result.filename}${result.page ? ` at page ${result.page}` : ''}`
  return (
    <li>
      <ResultButton type="button" onClick={() => onSelect(result)} aria-label={ariaLabel}>
        <FileIcon aria-hidden="true">{isCombined ? <MergeIcon /> : <PictureAsPdfOutlinedIcon />}</FileIcon>
        <TitleRow>
          <Filename title={result.filename}>{result.filename}</Filename>
          <Pill>
            {isCombined ? <MergeIcon /> : <PictureAsPdfOutlinedIcon />}
            {statusLabel(result.status)}
          </Pill>
        </TitleRow>
        <PageLabel>
          {pageText}
          <ChevronRightIcon aria-hidden="true" />
        </PageLabel>
        {isFilenameMatch ? <FilenameMatch>File name matches</FilenameMatch> : <Snippet>{renderHighlighted(result.snippet)}</Snippet>}
      </ResultButton>
    </li>
  )
}

ResultRow.propTypes = {
  result: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired
}

function RequestSearchResults({
  status,
  query,
  results = [],
  totals = null,
  indexing = { pending: 0, failed: 0 },
  errorMessage,
  onClose,
  onRetry,
  onSelectResult
}) {
  if (status === SEARCH_STATUS.idle) return null

  const pending = indexing?.pending ?? 0
  const failed = indexing?.failed ?? 0
  const shown = results.length
  const totalResults = totals?.results ?? shown
  const totalFiles = totals?.files ?? new Set(results.map(r => r.fileId)).size
  const quoted = `“${query}”`

  let title
  if (status === SEARCH_STATUS.searching) title = `Searching ${quoted} across all files in this request…`
  else if (status === SEARCH_STATUS.error) title = 'Search failed'
  else if (shown === 0) title = `No results for ${quoted}`
  else if (pending > 0) title = `${pluralize(totalResults, 'result')} for ${quoted} so far`
  else title = `${pluralize(totalResults, 'result')} for ${quoted} in ${pluralize(totalFiles, 'file')}`

  const groups = groupResultsByCategory(results)

  return (
    <Panel aria-label="Search results" aria-busy={status === SEARCH_STATUS.searching}>
      <Header>
        <HeaderTitle>{title}</HeaderTitle>
        <CloseButton type="button" onClick={onClose} aria-label="Close search results">
          <CloseIcon />
        </CloseButton>
      </Header>

      {status === SEARCH_STATUS.searching && (
        <SearchingRow role="status">
          <Spinner size={20} thickness={5} disableShrink />
          Looking through file names and page text…
        </SearchingRow>
      )}

      {status === SEARCH_STATUS.error && (
        <InfoRow>
          <FullWidthWrapper>
            <ErrorMessage>{errorMessage}</ErrorMessage>
          </FullWidthWrapper>
          <RetryButton type="button" onClick={onRetry} padding="short">
            <ReplayIcon /> Retry
          </RetryButton>
        </InfoRow>
      )}

      {status === SEARCH_STATUS.results && (
        <>
          {pending > 0 && (
            <InfoRow>
              <FullWidthWrapper role="status">
                <ProcessingMessage>
                  Indexing {pluralize(pending, 'file')} — results may be incomplete. Search again in a moment to include them.
                </ProcessingMessage>
              </FullWidthWrapper>
            </InfoRow>
          )}
          {failed > 0 && (
            <InfoRow>
              <FullWidthWrapper role="status" data-testid="indexing-failed-notice">
                <OrangeErrorMessage>
                  {pluralize(failed, 'file')} could not be indexed — {failed === 1 ? 'its' : 'their'} page text is not searchable (file names still
                  are). Replace or re-upload {failed === 1 ? 'the file' : 'those files'} to index {failed === 1 ? 'it' : 'them'} again.
                </OrangeErrorMessage>
              </FullWidthWrapper>
            </InfoRow>
          )}
          {shown === 0 ? (
            <CenteredState>
              <SearchOffIcon aria-hidden="true" />
              <StateTitle>Nothing matched {quoted}</StateTitle>
              <StateHint>File names and the text of converted and combined files are searched. Removed files and raw originals are not.</StateHint>
            </CenteredState>
          ) : (
            <GroupList>
              {groups.map(group => (
                <li key={group.categoryId}>
                  <GroupHeader>
                    <FolderOutlinedIcon aria-hidden="true" />
                    <span>{group.categoryName}</span>
                    <GroupCount>{pluralize(group.results.length, 'result')}</GroupCount>
                  </GroupHeader>
                  <ResultList>
                    {group.results.map(result => (
                      <ResultRow key={`${result.fileId}:${result.matchType}:${result.page ?? 'name'}`} result={result} onSelect={onSelectResult} />
                    ))}
                  </ResultList>
                </li>
              ))}
            </GroupList>
          )}
          {shown > 0 && (
            <Footer>
              {pending > 0
                ? `Showing ${pluralize(shown, 'result')} · more may appear once indexing finishes`
                : `Showing ${shown} of ${totalResults} · click a result to open the file at that page`}
            </Footer>
          )}
        </>
      )}
    </Panel>
  )
}

RequestSearchResults.propTypes = {
  status: PropTypes.oneOf(Object.values(SEARCH_STATUS)).isRequired,
  query: PropTypes.string,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
      categoryId: PropTypes.string.isRequired,
      categoryName: PropTypes.string,
      status: PropTypes.string.isRequired,
      page: PropTypes.number,
      snippet: PropTypes.string,
      rank: PropTypes.number,
      matchType: PropTypes.oneOf(['content', 'filename']).isRequired
    })
  ),
  totals: PropTypes.shape({ results: PropTypes.number, files: PropTypes.number }),
  indexing: PropTypes.shape({ pending: PropTypes.number, failed: PropTypes.number }),
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onSelectResult: PropTypes.func.isRequired
}

export default RequestSearchResults
